import React, { useState, useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';
import axios from 'axios';
import Icon from './Icon';
import BrandIcon from './BrandIcon';
import Avatar from './Avatar';
import Layout from './Layout';
import CalendarView from './CalendarView';
import MeetingsPanel from './MeetingsPanel';
import LiveKitMeetings from './LiveKitMeetings';
import MeetingScheduler from './MeetingScheduler';
import ToDoList from './ToDoList';
import ContentCreation from './ContentCreation';
import DashboardHome from './DashboardHome';
import BrandVoiceDesigner from './BrandVoiceDesigner';
import ProfilePanel from './ProfilePanel';
import NotificationsPanel from './NotificationsPanel';
import WelcomeModal from './WelcomeModal';
import OnboardingWizard from './OnboardingWizard';
import OnboardingRoleSelector from './OnboardingRoleSelector';
import CommentModal from './CommentModal';
import { getUserRole } from '../utils/rolePermissions';
import { useModal } from '../context/ModalContext';
import { useOrgBrand } from '../context/OrgBrandContext';
import { getUsableOriginalUrl } from '../utils/imageUrl';
import { formatBlogBody } from '../utils/blogFormatting';
import '../styles/ClientDashboard.css';
import '../styles/OperatorImageModal.css';

const ClientDashboard = ({ user, onLogout, onUserUpdate, isProxyMode = false }) => {
  const [searchParams, setSearchParams] = useSearchParams();
  const modal = useModal();
  const { selectedBrand, selectedOrganization } = useOrgBrand();
  const [dashboardData, setDashboardData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [warning, setWarning] = useState(null);
  const [ideasItems, setIdeasItems] = useState([]);
  const [ideasLoading, setIdeasLoading] = useState(false);
  const [contentDrafts, setContentDrafts] = useState([]);
  const [contentLoading, setContentLoading] = useState(false);
  const [viewingDraft, setViewingDraft] = useState(null);
  const [viewCommentText, setViewCommentText] = useState('');

  // Tab state for Review Ideas and Review Content
  const [reviewIdeasTab, setReviewIdeasTab] = useState('review'); // 'review' or 'history'
  const [reviewContentTab, setReviewContentTab] = useState('review'); // 'review' or 'history'
  const [ideasHistory, setIdeasHistory] = useState([]);
  const [contentHistory, setContentHistory] = useState([]);
  const [ideasHistoryLoading, setIdeasHistoryLoading] = useState(false);
  const [contentHistoryLoading, setContentHistoryLoading] = useState(false);

  // Revision modal state
  const [revisionModal, setRevisionModal] = useState({
    open: false,
    type: null, // 'content' or 'idea'
    decision: null, // 'revision_requested' or 'denied'
    itemId: null,
    docId: null, // for ideas
    ideaId: null // for ideas
  });
  const [revisionComment, setRevisionComment] = useState('');

  // Comment modal state
  const [commentModalState, setCommentModalState] = useState({ open: false, draftId: null, comments: [] });
  const [ideaCommentModalState, setIdeaCommentModalState] = useState({ open: false, docId: null, ideaId: null, comments: [] });

  // In proxy mode, use local state instead of URL params to avoid affecting parent
  const [localActiveSection, setLocalActiveSection] = useState('dashboard');
  const [showWelcomeModal, setShowWelcomeModal] = useState(false);
  const [showOnboardingWizard, setShowOnboardingWizard] = useState(false);
  const [showOnboardingRoleSelector, setShowOnboardingRoleSelector] = useState(false);
  const [selectedOnboardingRole, setSelectedOnboardingRole] = useState(null);

  // Read activeSection from URL params (if not in proxy mode) or local state (if in proxy mode)
  const activeSection = isProxyMode ? localActiveSection : (searchParams.get('section') || 'dashboard');

  // Update URL or local state when section changes
  const setActiveSection = (section) => {
    if (isProxyMode) {
      setLocalActiveSection(section);
    } else {
      setSearchParams({ section });
    }
  };

  useEffect(() => {
    fetchDashboardData();
    if (activeSection === 'client-review-ideas') {
      fetchIdeasItems();
    }
    if (activeSection === 'review-content') {
      fetchContentDrafts();
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeSection]);

  useEffect(() => {
    if (user && !isProxyMode) {
      if (user.welcome_seen === false) {
        setShowWelcomeModal(true);
      } else if (user.onboarding_seen === false) {
        setShowOnboardingWizard(true);
      }
    }
  }, [user, isProxyMode]);

  const handleOpenWelcome = () => {
    setShowWelcomeModal(true);
  };

  const handleWelcomeComplete = async () => {
    try {
      await axios.post('/api/user/mark-welcome-seen');
      if (user.onboarding_seen === false) {
        setShowOnboardingWizard(true);
      }
    } catch (error) {
      console.error('Failed to mark welcome as seen:', error);
    }
  };

  const handleOpenOnboarding = () => {
    setShowOnboardingWizard(true);
  };

  const handleRoleSelect = (role) => {
    setSelectedOnboardingRole(role);
    setShowOnboardingWizard(true);
  };

  const handleOnboardingComplete = async () => {
    try {
      await axios.post('/api/user/mark-onboarding-seen');
    } catch (error) {
      console.error('Failed to mark onboarding as seen:', error);
    }
  };

  const handleOnboardingSkip = async () => {
    try {
      await axios.post('/api/user/mark-onboarding-seen');
    } catch (error) {
      console.error('Failed to mark onboarding as seen:', error);
    }
  };

  // Refetch ideas and content when selected brand changes
  useEffect(() => {
    if (activeSection === 'client-review-ideas') {
      if (reviewIdeasTab === 'review') {
        fetchIdeasItems();
      } else if (reviewIdeasTab === 'history') {
        fetchIdeasHistory();
      }
    }
    if (activeSection === 'review-content') {
      if (reviewContentTab === 'review') {
        fetchContentDrafts();
      } else if (reviewContentTab === 'history') {
        fetchContentHistory();
      }
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedBrand]);

  // Fetch history when tab changes
  useEffect(() => {
    if (activeSection === 'client-review-ideas' && reviewIdeasTab === 'history') {
      fetchIdeasHistory();
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [reviewIdeasTab]);

  useEffect(() => {
    if (activeSection === 'review-content' && reviewContentTab === 'history') {
      fetchContentHistory();
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [reviewContentTab]);

  const fetchDashboardData = async () => {
    try {
      setLoading(true);
      // In proxy mode, the API call uses super admin's session which will work
      // because the backend checks for proxied_user_id in session
      const response = await axios.get('/api/client/dashboard');
      setDashboardData(response.data);
      // Check for warning in response (e.g., no agency assigned)
      if (response.data?.warning) {
        setWarning(response.data.warning);
      } else {
        setWarning(null);
      }
    } catch (error) {
      console.error('Error fetching dashboard data:', error);
      // Don't show error in proxy mode as the super admin may not have client assignments
      if (!isProxyMode) {
        modal.error('Failed to load dashboard data');
      }
    } finally {
      setLoading(false);
    }
  };

  const fetchIdeasItems = async () => {
    try {
      setIdeasLoading(true);

      // Build query parameters based on selected brand/org
      const params = {};
      if (selectedBrand && selectedBrand !== 'all') {
        const brandId = selectedBrand._id || selectedBrand.id;
        const orgId = selectedBrand.organization_id || selectedBrand.organizationId;

        if (brandId) {
          params.brand_id = brandId;
        }
        if (orgId) {
          params.organization_id = orgId;
        }
      }
      if (selectedOrganization && selectedOrganization !== 'all') {
        const orgId = selectedOrganization._id || selectedOrganization.id;
        params.organization_id = orgId;
      }

      const response = await axios.get('/api/ideas', {
        params,
        withCredentials: true
      });
      const allIdeas = response.data.ideas || [];
      const flattenedIdeas = [];

      allIdeas.forEach(doc => {
        // No need for client-side filtering anymore - server handles it
        (doc.ideas || []).forEach(idea => {
          if (idea.status === 'sent_to_client') {
            flattenedIdeas.push({
              ...idea,
              docId: doc._id,
              brandName: doc.brand?.name || doc.brand?.brand_name || 'Unknown Brand',
              brandLogo: doc.brand?.icon_url,
              organizationName: doc.organization?.name || doc.organization?.organization_name || 'Unknown Organization',
              generatedAt: doc.generated_at
            });
          }
        });
      });

      // Sort by sent_to_client_at descending (newest first)
      flattenedIdeas.sort((a, b) => {
        const dateA = new Date(a.sent_to_client_at || a.generatedAt || 0);
        const dateB = new Date(b.sent_to_client_at || b.generatedAt || 0);
        return dateB - dateA;
      });

      setIdeasItems(flattenedIdeas);
    } catch (error) {
      console.error('Error fetching ideas:', error);
    } finally {
      setIdeasLoading(false);
    }
  };

  const handleIdeaDecision = async (docId, ideaId, decision, comment = null) => {
    // If revision requested or denied without comment, open modal
    if ((decision === 'revision_requested' || decision === 'denied') && !comment) {
      setRevisionModal({
        open: true,
        type: 'idea',
        decision: decision,
        itemId: null,
        docId: docId,
        ideaId: ideaId
      });
      setRevisionComment('');
      return;
    }

    if (decision === 'approved') {
      const confirmed = await modal.confirm('Are you sure you want to approve this idea?', 'Confirm Action');
      if (!confirmed) return;
    }

    try {
      await axios.put(`/api/ideas/${docId}/idea/${ideaId}/status`, {
        status: decision,
        comment: comment || undefined
      }, { withCredentials: true });
      fetchIdeasItems();
    } catch (error) {
      console.error('Error updating idea decision:', error);
      modal.error(error.response?.data?.error || 'Failed to update idea');
    }
  };

  const fetchContentDrafts = async () => {
    try {
      setContentLoading(true);

      // Build query parameters based on selected brand/org
      const params = {};
      if (selectedBrand && selectedBrand !== 'all') {
        const brandId = selectedBrand._id || selectedBrand.id;
        const orgId = selectedBrand.organization_id || selectedBrand.organizationId;

        if (brandId) {
          params.brand_id = brandId;
        }
        if (orgId) {
          params.organization_id = orgId;
        }
      }
      if (selectedOrganization && selectedOrganization !== 'all') {
        const orgId = selectedOrganization._id || selectedOrganization.id;
        params.organization_id = orgId;
      }

      const response = await axios.get('/api/content-drafts', {
        params,
        withCredentials: true
      });
      // Clients only see drafts that have been sent to them
      const all = response.data.drafts || [];

      // No need for client-side filtering anymore - server handles it
      const filtered = all.filter(d => d.status === 'sent_to_client');

      // Sort by sent_to_client_at descending (newest first)
      filtered.sort((a, b) => {
        const dateA = new Date(a.sent_to_client_at || a.created_at || 0);
        const dateB = new Date(b.sent_to_client_at || b.created_at || 0);
        return dateB - dateA;
      });

      setContentDrafts(filtered);
    } catch (error) {
      console.error('Error fetching content drafts:', error);
    } finally {
      setContentLoading(false);
    }
  };

  const fetchIdeasHistory = async () => {
    try {
      setIdeasHistoryLoading(true);

      // Fetch all history without backend filters (like ToDoList does)
      const response = await axios.get('/api/todo-history', {
        params: { entity_type: 'idea' },
        withCredentials: true
      });

      // Filter on frontend based on selected brand/org
      let ideasOnly = (response.data.history || []).filter(item => {
        // Only show ideas where client took action
        if (item.itemType !== 'idea') return false;
        // Exclude sent_to_client (pending, not acted upon yet)
        if (item.status === 'sent_to_client') return false;
        // Only show statuses where client responded
        return ['approved', 'denied', 'revision_requested'].includes(item.status);
      });

      // Apply brand/org filtering on frontend
      if (selectedBrand && selectedBrand !== 'all') {
        const brandId = selectedBrand._id || selectedBrand.id;
        ideasOnly = ideasOnly.filter(item => item.brand_id === brandId);
      } else if (selectedOrganization && selectedOrganization !== 'all') {
        const orgId = selectedOrganization._id || selectedOrganization.id;
        ideasOnly = ideasOnly.filter(item => item.organization_id === orgId);
      }

      // Sort by action date descending (most recent action first)
      ideasOnly.sort((a, b) => {
        const dateA = new Date(a.responded_at || a.client_decision_at || a.sent_to_client_at || a.updated_at || a.generatedAt || 0);
        const dateB = new Date(b.responded_at || b.client_decision_at || b.sent_to_client_at || b.updated_at || b.generatedAt || 0);
        return dateB - dateA;
      });

      setIdeasHistory(ideasOnly);
    } catch (error) {
      console.error('Error fetching ideas history:', error);
      setIdeasHistory([]);
    } finally {
      setIdeasHistoryLoading(false);
    }
  };

  const fetchContentHistory = async () => {
    try {
      setContentHistoryLoading(true);

      // Fetch all history without backend filters (like ToDoList does)
      const response = await axios.get('/api/todo-history', {
        params: { entity_type: 'content_draft' },
        withCredentials: true
      });

      // Filter on frontend based on selected brand/org
      let contentDraftsOnly = (response.data.history || []).filter(item => {
        // Only show content drafts where client took action
        if (item.itemType !== 'content_draft') return false;
        // Exclude sent_to_client (pending, not acted upon yet)
        if (item.status === 'sent_to_client') return false;
        // Only show statuses where client responded (approve, deny, or request revision)
        return ['approved', 'denied', 'revision_requested'].includes(item.status);
      });

      // Apply brand/org filtering on frontend
      if (selectedBrand && selectedBrand !== 'all') {
        const brandId = selectedBrand._id || selectedBrand.id;
        contentDraftsOnly = contentDraftsOnly.filter(item =>
          item.brand_id === brandId || item.brand?._id === brandId || item.brand?.id === brandId
        );
      } else if (selectedOrganization && selectedOrganization !== 'all') {
        const orgId = selectedOrganization._id || selectedOrganization.id;
        contentDraftsOnly = contentDraftsOnly.filter(item =>
          item.organization_id === orgId || item.organization?._id === orgId || item.organization?.id === orgId
        );
      }

      // Sort by action date descending (most recent action first)
      contentDraftsOnly.sort((a, b) => {
        const dateA = new Date(a.responded_at || a.client_decision_at || a.sent_to_client_at || a.updated_at || a.created_at || 0);
        const dateB = new Date(b.responded_at || b.client_decision_at || b.sent_to_client_at || b.updated_at || b.created_at || 0);
        return dateB - dateA;
      });

      setContentHistory(contentDraftsOnly);
    } catch (error) {
      console.error('Error fetching content history:', error);
      setContentHistory([]);
    } finally {
      setContentHistoryLoading(false);
    }
  };

  const handleContentDecision = async (draftId, decision, comment = null) => {
    // If revision requested or denied without comment, open modal
    if ((decision === 'revision_requested' || decision === 'denied') && !comment) {
      setRevisionModal({
        open: true,
        type: 'content',
        decision: decision,
        itemId: draftId,
        docId: null,
        ideaId: null
      });
      setRevisionComment('');
      // Close viewing draft if open
      setViewingDraft(null);
      return;
    }

    if (decision === 'approved') {
      const confirmed = await modal.confirm('Are you sure you want to approve this content?', 'Confirm Action');
      if (!confirmed) return;
    }

    try {
      await axios.put(`/api/content-drafts/${draftId}/status`, {
        status: decision,
        comment: comment || undefined
      }, { withCredentials: true });
      fetchContentDrafts();
      // Close viewing draft if open
      if (viewingDraft && viewingDraft._id === draftId) {
        setViewingDraft(null);
      }
    } catch (error) {
      console.error('Error updating content decision:', error);
      modal.error(error.response?.data?.error || 'Failed to update content');
    }
  };

  const handleAddViewComment = async () => {
    if (!viewCommentText.trim() || !viewingDraft) return;
    try {
      await axios.post(
        `/api/content-drafts/${viewingDraft._id}/comment`,
        { text: viewCommentText },
        { withCredentials: true }
      );
      setViewCommentText('');

      // Build query parameters based on selected brand/org
      const params = {};
      if (selectedBrand) {
        const brandId = selectedBrand._id || selectedBrand.id;
        const orgId = selectedBrand.organization_id || selectedBrand.organizationId;

        if (brandId) {
          params.brand_id = brandId;
        }
        if (orgId) {
          params.organization_id = orgId;
        }
      }

      const response = await axios.get('/api/content-drafts', {
        params,
        withCredentials: true
      });
      const all = response.data.drafts || [];
      const updated = all.find(d => d._id === viewingDraft._id);
      if (updated) setViewingDraft(updated);
      fetchContentDrafts();
    } catch (error) {
      console.error('Error adding comment:', error);
      modal.error(error.response?.data?.error || 'Failed to add comment');
    }
  };

  // Handle revision modal submission
  const handleRevisionSubmit = async () => {
    if (!revisionComment.trim()) {
      const actionText = revisionModal.decision === 'denied' ? 'denying' : 'requesting a revision';
      modal.warning(`Please enter a comment explaining why you are ${actionText}`);
      return;
    }

    if (revisionModal.type === 'content') {
      await handleContentDecision(revisionModal.itemId, revisionModal.decision, revisionComment);
    } else if (revisionModal.type === 'idea') {
      await handleIdeaDecision(revisionModal.docId, revisionModal.ideaId, revisionModal.decision, revisionComment);
    }

    // Close modal and reset
    setRevisionModal({ open: false, type: null, decision: null, itemId: null, docId: null, ideaId: null });
    setRevisionComment('');
  };



  const formatDate = (date) => {
    if (!date) return '-';
    return new Date(date).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
  };

  const formatCommentTimestamp = (dateString) => {
    if (!dateString) return '';
    const date = new Date(dateString);
    const month = date.getMonth() + 1;
    const day = date.getDate();
    let hours = date.getHours();
    const minutes = date.getMinutes();
    const ampm = hours >= 12 ? 'PM' : 'AM';
    hours = hours % 12 || 12;
    const minutesStr = minutes < 10 ? '0' + minutes : minutes;
    return `${month}/${day} ${hours}:${minutesStr} ${ampm}`;
  };

  const getContextMenuTitle = () => {
    switch (activeSection) {
      case 'dashboard':
        return 'Dashboard';
      case 'todo':
        return 'To Do List';
      case 'create':
        return 'Generate Ideas';
      case 'client-review-ideas':
        return 'Review Ideas';
      case 'review-content':
        return 'Review Content';
      case 'calendar':
        return 'Calendar';
      case 'calendar-scheduler':
        return 'Schedule Meeting';
      case 'livekit-meetings':
        return 'Velvet Meetings';
      case 'organizations':
        return 'Organizations';
      case 'brands':
        return 'Brands';
      case 'brand-builder':
        return 'Brand Voice Designer';
      case 'settings':
        return 'Settings';
      default:
        return 'Dashboard';
    }
  };

  const renderContent = () => {
    switch (activeSection) {
      case 'dashboard':
        return <DashboardHome user={user} onSectionChange={setActiveSection} isProxyMode={isProxyMode} />;
      case 'todo':
        return <ToDoList user={user} onSectionChange={setActiveSection} isProxyMode={isProxyMode} />;
      case 'create':
        return <ContentCreation user={user} />;
      case 'client-review-ideas':
        return renderReviewIdeas();
      case 'review-content':
        return renderReviewContent();
      case 'calendar':
      case 'calendar-scheduler':
        return <MeetingsPanel user={user} />;
      case 'livekit-meetings':
        return <LiveKitMeetings user={user} />;
      case 'organizations':
        return renderOrganizations();
      case 'brands':
        return renderBrands();
      case 'brand-builder':
        return <BrandVoiceDesigner user={user} />;
      case 'settings':
        return renderSettings();
      case 'profile':
        return <ProfilePanel user={user} onUserUpdate={onUserUpdate} />;
      case 'notifications':
        return <NotificationsPanel user={user} />;
      default:
        return renderOverview();
    }
  };

  const renderReviewIdeas = () => (
    <div className="client-review-wrapper">
      {/* Tabs Container */}
      <div className="client-review-tabs-container">
        <button
          className={`client-review-tab ${reviewIdeasTab === 'review' ? 'active' : ''}`}
          onClick={() => setReviewIdeasTab('review')}
        >
          Review
        </button>
        <button
          className={`client-review-tab ${reviewIdeasTab === 'history' ? 'active' : ''}`}
          onClick={() => setReviewIdeasTab('history')}
        >
          History
        </button>
      </div>

      {reviewIdeasTab === 'history' ? (
        /* History Tab */
        ideasHistoryLoading ? (
          <div className="client-review-loading">
            <div className="client-review-spinner"></div>
            <p>Loading history...</p>
          </div>
        ) : ideasHistory.length === 0 ? (
          <div className="client-review-empty">
            <div className="client-review-empty-icon"><Icon name="lightbulb" size="3rem" /></div>
            <h3>No Ideas History Yet</h3>
            <p>Approved, denied, and archived ideas will show up here once there's activity.</p>
          </div>
        ) : (
          <div className="client-review-table-card">
            <table className="client-review-table">
              <thead>
                <tr>
                  <th>Brand</th>
                  <th>Idea</th>
                  <th>Synopsis</th>
                  <th>Action Date</th>
                  <th>Status</th>
                  <th>Comments</th>
                </tr>
              </thead>
              <tbody>
                {ideasHistory.map((idea, index) => (
                  <tr key={index}>
                    <td className="cr-brand-cell">
                      {idea.brand_logo ? (
                        <BrandIcon src={idea.brand_logo} alt={idea.brandName} className="cr-brand-logo" />
                      ) : (
                        <div className="cr-brand-placeholder">
                          <span>{(idea.brandName || 'B').charAt(0).toUpperCase()}</span>
                        </div>
                      )}
                    </td>
                    <td className="cr-post-cell">
                      <div className="cr-post-content">
                        <strong>{idea.title || 'Untitled Idea'}</strong>
                      </div>
                    </td>
                    <td className="cr-synopsis-cell">
                      <span className="cr-synopsis-text">{idea.synopsis || idea.description || 'No synopsis available'}</span>
                    </td>
                    <td className="cr-date-cell">{formatDate(idea.responded_at || idea.client_decision_at || idea.sent_to_client_at || idea.updated_at || idea.generatedAt)}</td>
                    <td className="cr-approve-cell">
                      {idea.status === 'approved' && (
                        <span className="history-badge approved">Approved</span>
                      )}
                      {idea.status === 'denied' && (
                        <span className="history-badge denied">Denied</span>
                      )}
                      {idea.status === 'revision_requested' && (
                        <span className="history-badge revision">Revision Requested</span>
                      )}
                    </td>
                    <td className="cr-comment-cell">
                      <button
                        className="cr-comment-icon-btn"
                        onClick={(e) => {
                          e.preventDefault();
                          e.stopPropagation();
                          setIdeaCommentModalState({
                            open: true,
                            docId: idea.docId,
                            ideaId: idea.id,
                            comments: idea.comments || []
                          });
                        }}
                        title="View comments"
                        type="button"
                      >
                        <Icon name="comment-empty" size="1.5rem" />
                        {idea.comments && idea.comments.length > 0 && (
                          <span className="cr-comment-count-badge">{idea.comments.length}</span>
                        )}
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )
      ) : (
        /* Review Tab */
        ideasLoading ? (
          <div className="client-review-loading">
            <div className="client-review-spinner"></div>
            <p>Loading ideas...</p>
          </div>
        ) : ideasItems.length === 0 ? (
          <div className="client-review-empty">
            <div className="client-review-empty-icon"><Icon name="lightbulb" size="3rem" /></div>
            <h3>No Ideas Pending Review</h3>
            <p>New ideas will show up here as soon as they're ready for you to review.</p>
          </div>
        ) : (
        <div className="client-review-table-card">
          <table className="client-review-table">
            <thead>
              <tr>
                <th>Brand</th>
                <th>Idea</th>
                <th>Synopsis</th>
                <th>Received</th>
                <th>Actions</th>
                <th>Comments</th>
              </tr>
            </thead>
            <tbody>
              {ideasItems.map((idea) => (
                <tr key={`${idea.docId}-${idea.id}`}>
                  <td className="cr-brand-cell">
                    {idea.brandLogo ? (
                      <BrandIcon src={idea.brandLogo} alt={idea.brandName} className="cr-brand-logo" />
                    ) : (
                      <div className="cr-brand-placeholder">
                        <span>{(idea.brandName || 'B').charAt(0).toUpperCase()}</span>
                      </div>
                    )}
                  </td>
                  <td className="cr-post-cell">
                    <div className="cr-post-content">
                      <strong>{idea.title || 'Untitled Idea'}</strong>
                      <span className="cr-org-name">{idea.organizationName}</span>
                    </div>
                  </td>
                  <td className="cr-synopsis-cell">
                    <span className="cr-synopsis-text">{idea.synopsis || 'No synopsis available'}</span>
                  </td>
                  <td className="cr-date-cell">{formatDate(idea.sent_to_client_at || idea.generatedAt)}</td>
                  <td className="cr-approve-cell">
                    <div className="cr-approve-buttons">
                      <button
                        className="cr-approve-btn approve"
                        onClick={() => handleIdeaDecision(idea.docId, idea.id, 'approved')}
                        title="Approve"
                      >
                        <Icon name="ok" size="1.25rem" />
                      </button>
                      <button
                        className="cr-approve-btn flag"
                        onClick={() => handleIdeaDecision(idea.docId, idea.id, 'revision_requested')}
                        title="Request revision"
                      >
                        <Icon name="flag" size="1.25rem" />
                      </button>
                      <button
                        className="cr-approve-btn deny"
                        onClick={() => handleIdeaDecision(idea.docId, idea.id, 'denied')}
                        title="Deny"
                      >
                        <Icon name="cancel" size="1.25rem" />
                      </button>
                    </div>
                  </td>
                  <td className="cr-comment-cell">
                    <button
                      className="cr-comment-icon-btn"
                      onClick={(e) => {
                        e.preventDefault();
                        e.stopPropagation();
                        setIdeaCommentModalState({
                          open: true,
                          docId: idea.docId,
                          ideaId: idea.id,
                          comments: idea.comments || []
                        });
                      }}
                      title="View comments"
                      type="button"
                    >
                      <Icon name="comment-empty" size="1.5rem" />
                      {idea.comments && idea.comments.length > 0 && (
                        <span className="cr-comment-count-badge">{idea.comments.length}</span>
                      )}
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        )
      )}
    </div>
  );

  const contentTypeConfig = {
    social:     { label: 'Social',     color: '#3B82F6' },
    blog:       { label: 'Blog',       color: '#10B981' },
    video:      { label: 'Video',      color: '#EF4444' },
    podcast:    { label: 'Podcast',    color: '#8B5CF6' },
    newsletter: { label: 'Newsletter', color: '#F59E0B' }
  };
  const getContentTypeBadgeColor = (type) => (contentTypeConfig[type]?.color || '#6B7280');
  const getContentTypeLabel = (type) => (contentTypeConfig[type]?.label || (type ? type.charAt(0).toUpperCase() + type.slice(1) : '-'));

  const getPreviewImageStyle = (draft) => {
    if (!draft.media_url || !draft.media_crop_data) {
      return {};
    }

    const cropData = draft.media_crop_data;
    const imageDimensions = { width: 1, height: 1 };

    const containerAspect = 4 / 3;
    const imageAspect = imageDimensions.width / imageDimensions.height;

    const editorWidth = 450;
    const editorHeight = editorWidth / containerAspect;

    const cropPercentX = (cropData.cropPosition.x / editorWidth) * 100;
    const cropPercentY = (cropData.cropPosition.y / editorHeight) * 100;

    return {
      position: 'absolute',
      top: '50%',
      left: '50%',
      transform: `translate(calc(-50% + ${cropPercentX}%), calc(-50% + ${cropPercentY}%)) scale(${cropData.zoom})`,
      transformOrigin: 'center',
      width: imageAspect > containerAspect ? 'auto' : '100%',
      height: imageAspect > containerAspect ? '100%' : 'auto',
      maxWidth: 'none',
      maxHeight: 'none',
    };
  };

  const renderContentMockModal = () => {
    if (!viewingDraft) return null;
    const draft = viewingDraft;
    const brandName = draft.brand?.name || 'Brand';
    const brandLogo = draft.brand?.icon_url;
    const isSocial = draft.content_type === 'social';
    const isBlog = draft.content_type === 'blog';
    const dateStr = formatDate(draft.client_decision_at || draft.created_at);

    const extractHashtags = (text) => (text || '').match(/#\w+/g) || [];
    const hashtags = [...new Set([
      ...extractHashtags(draft.production_notes || ''),
      ...extractHashtags(draft.body || '')
    ])];
    const cleanBody = (draft.body || '').replace(/#\w+/g, '').trim();

    const renderMedia = () => draft.media_url && (
      <div
        className="mock-media-wrapper"
        style={draft.background_color && draft.background_color !== 'transparent' ? { backgroundColor: draft.background_color } : undefined}
      >
        {draft.media_crop_data && getUsableOriginalUrl(draft) ? (
          <div style={{ position: 'relative', width: '100%', height: '100%', overflow: 'hidden' }}>
            <img
              src={getUsableOriginalUrl(draft)}
              alt="Preview"
              style={getPreviewImageStyle(draft)}
            />
          </div>
        ) : (
          <img src={draft.media_url} alt="Content media" className="mock-post-image" />
        )}
      </div>
    );

    return (
      <div className="mock-modal-overlay">
        <div className="mock-modal">
          <div className="mock-modal-header">
            <h3>Content Preview</h3>
            <button className="modal-close-btn" onClick={() => setViewingDraft(null)} aria-label="Close" />
          </div>

          <div className="mock-modal-body">
            {/* Post Preview - using same structure as OperatorImageModal */}
            <div className="op-image-preview-mock">
              {isSocial ? (
                <div className="mock-social-post">
                  <div className="mock-post-header">
                    <div className="mock-author-info">
                      {brandLogo ? (
                        <BrandIcon src={brandLogo} alt={brandName} className="mock-brand-avatar" />
                      ) : (
                        <div className="mock-brand-initial">{brandName ? brandName.charAt(0).toUpperCase() : 'B'}</div>
                      )}
                      <div>
                        <div className="mock-brand-name">{brandName || 'Brand Name'}</div>
                        <div className="mock-post-meta">{dateStr} · <Icon name="link" size="0.9rem" /></div>
                      </div>
                    </div>
                    <span className="mock-type-tag mock-type-social">SOCIAL</span>
                  </div>
                  <div className="mock-post-body">
                    {formatBlogBody(cleanBody || draft.body || 'Content preview').map(block => (
                      block.type === 'heading'
                        ? <h4 key={block.key} className="mock-blog-subheading">{block.text}</h4>
                        : <p key={block.key} className="mock-post-paragraph">{block.text}</p>
                    ))}
                  </div>
                  {hashtags.length > 0 && (
                    <div className="mock-hashtags">
                      {hashtags.map((h, i) => <span key={i} className="mock-hashtag">{h} </span>)}
                    </div>
                  )}
                  {renderMedia()}
                  <div className="mock-social-actions">
                    <span className="mock-social-btn" style={{ color: 'rgb(225, 48, 108)' }}><Icon name="heart" size="0.9rem" /> Like</span>
                    <span className="mock-social-btn"><Icon name="comment-empty" size="0.9rem" /> Comment</span>
                    <span className="mock-social-btn"><Icon name="share" size="0.9rem" /> Share</span>
                    <span className="mock-social-btn"><Icon name="paper-plane-empty" size="0.9rem" /> Send</span>
                  </div>
                </div>
              ) : isBlog ? (
                <div className="mock-blog-post">
                  <div className="mock-blog-url-bar">
                    <span className="mock-blog-url">
                      {draft.call_to_action || `https://blog.example.com/${(draft.headline || draft.title || 'post').toLowerCase().replace(/\s+/g, '-')}`}
                    </span>
                    <span className="mock-type-tag mock-type-blog">BLOG</span>
                  </div>
                  {draft.media_url && (
                    <div className="mock-blog-image-wrapper">
                      <img src={draft.media_url} alt="Blog header" className="mock-blog-image" />
                    </div>
                  )}
                  {draft.headline && <h2 className="mock-blog-title">{draft.headline}</h2>}
                  <div className="mock-blog-date">{dateStr}</div>
                  <div className="mock-blog-body">
                    {formatBlogBody(draft.body).map(block => (
                      block.type === 'heading'
                        ? <h4 key={block.key} className="mock-blog-subheading">{block.text}</h4>
                        : <p key={block.key} className="mock-blog-paragraph">{block.text}</p>
                    ))}
                  </div>
                </div>
              ) : (
                <div className="mock-generic-post">
                  <div className="mock-post-header">
                    <div className="mock-author-info">
                      {brandLogo ? (
                        <BrandIcon src={brandLogo} alt={brandName} className="mock-brand-avatar" />
                      ) : (
                        <div className="mock-brand-initial">{brandName ? brandName.charAt(0).toUpperCase() : 'B'}</div>
                      )}
                      <div>
                        <div className="mock-brand-name">{brandName || 'Brand Name'}</div>
                        <div className="mock-post-meta">{dateStr}</div>
                      </div>
                    </div>
                    {draft.content_type && (
                      <span className="mock-type-tag" style={{ backgroundColor: getContentTypeBadgeColor(draft.content_type) }}>
                        {getContentTypeLabel(draft.content_type).toUpperCase()}
                      </span>
                    )}
                  </div>
                  {draft.headline && <div className="mock-post-title">{draft.headline}</div>}
                  <div className="mock-post-body">
                    {formatBlogBody(draft.body).map(block => (
                      block.type === 'heading'
                        ? <h4 key={block.key} className="mock-blog-subheading">{block.text}</h4>
                        : <p key={block.key} className="mock-post-paragraph">{block.text}</p>
                    ))}
                  </div>
                  {renderMedia()}
                </div>
              )}
            </div>

            <div className="mock-approval-section">
              <div className="mock-comment-row">
                <Avatar user={user} size="sm" className="mock-user-avatar" />
                <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', flex: 1 }}>
                  <input
                    className="mock-comment-input"
                    placeholder="Add a comment"
                    value={viewCommentText}
                    onChange={(e) => setViewCommentText(e.target.value)}
                    onKeyDown={(e) => e.key === 'Enter' && handleAddViewComment()}
                    style={{ flex: 1 }}
                    autoComplete="off"
                    data-1p-ignore="true"
                    data-lpignore="true"
                    data-bwignore="true"
                    data-form-type="other"
                  />
                  <button
                    className="mock-comment-submit-btn"
                    onClick={handleAddViewComment}
                    disabled={!viewCommentText.trim()}
                    title="Submit comment"
                    style={{
                      padding: '0.5rem 1rem',
                      fontSize: '0.875rem',
                      fontWeight: 500,
                      background: viewCommentText.trim() ? 'var(--color-brand-blue)' : 'var(--color-preview-divider)',
                      color: 'var(--color-text-on-accent)',
                      border: 'none',
                      borderRadius: '6px',
                      cursor: viewCommentText.trim() ? 'pointer' : 'not-allowed',
                      transition: 'background 0.2s'
                    }}
                  >
                    Comment
                  </button>
                </div>
                <div className="mock-approval-btns">
                  <button
                    className="mock-approve-btn approve"
                    title="Approve"
                    onClick={() => { handleContentDecision(draft._id, 'approved'); setViewingDraft(null); }}
                  >
                    <Icon name="ok" size="1.125rem" />
                  </button>
                  <button
                    className="mock-approve-btn flag"
                    title="Request revision"
                    onClick={() => { handleContentDecision(draft._id, 'revision_requested'); setViewingDraft(null); }}
                  >
                    <Icon name="flag" size="1.125rem" />
                  </button>
                  <button
                    className="mock-approve-btn deny"
                    title="Deny"
                    onClick={() => { handleContentDecision(draft._id, 'denied'); setViewingDraft(null); }}
                  >
                    <Icon name="cancel" size="1.125rem" />
                  </button>
                </div>
              </div>

              {draft.comments && draft.comments.length > 0 && (
                <div className="mock-comments-list">
                  {draft.comments.map((c, i) => (
                    <div key={i} className="mock-comment-item">
                      <Avatar
                        user={{ _id: c.user_id, agency_id: c.agency_id, name: c.user_name }}
                        size="sm"
                        className="mock-user-avatar"
                      />
                      <div className="mock-comment-content">
                        <div className="mock-comment-author">{c.user_name}</div>
                        <div className="mock-comment-time">{formatDate(c.created_at)}</div>
                        <div className="mock-comment-text">{c.text}</div>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    );
  };

  const renderReviewContent = () => (
    <>
      {renderContentMockModal()}
      <div className="client-review-wrapper">
        {/* Tabs Container */}
        <div className="client-review-tabs-container">
          <button
            className={`client-review-tab ${reviewContentTab === 'review' ? 'active' : ''}`}
            onClick={() => setReviewContentTab('review')}
          >
            Review
          </button>
          <button
            className={`client-review-tab ${reviewContentTab === 'history' ? 'active' : ''}`}
            onClick={() => setReviewContentTab('history')}
          >
            History
          </button>
        </div>

        {reviewContentTab === 'history' ? (
          /* History Tab */
          contentHistoryLoading ? (
            <div className="client-review-loading">
              <div className="client-review-spinner"></div>
              <p>Loading history...</p>
            </div>
          ) : contentHistory.length === 0 ? (
            <div className="client-review-empty">
              <div className="client-review-empty-icon"><Icon name="content" size="3rem" /></div>
              <h3>No History Yet</h3>
              <p>Approved, denied, and archived content will show up here once there's activity.</p>
            </div>
          ) : (
            <div className="client-review-table-card">
              <table className="client-review-table">
                <thead>
                  <tr>
                    <th>Brand</th>
                    <th>Content</th>
                    <th>Preview</th>
                    <th>Type</th>
                    <th>Action Date</th>
                    <th>Status</th>
                    <th>Comments</th>
                    <th>View</th>
                  </tr>
                </thead>
                <tbody>
                  {contentHistory.map((draft, index) => (
                    <tr key={index}>
                      <td className="cr-brand-cell">
                        {draft.brand_logo ? (
                          <BrandIcon src={draft.brand_logo} alt={draft.brandName || draft.brand?.name} className="cr-brand-logo" />
                        ) : (
                          <div className="cr-brand-placeholder">
                            <span>{(draft.brandName || draft.brand?.name || 'B').charAt(0).toUpperCase()}</span>
                          </div>
                        )}
                      </td>
                      <td className="cr-post-cell">
                        <div className="cr-post-content">
                          <strong>{draft.headline || draft.title || 'Untitled'}</strong>
                        </div>
                      </td>
                      <td className="cr-synopsis-cell">
                        <span className="cr-synopsis-text">
                          {draft.body || 'No preview available'}
                        </span>
                      </td>
                      <td className="cr-type-cell">
                        {draft.content_type && (
                          <span className="cr-type-badge" style={{ backgroundColor: getContentTypeBadgeColor(draft.content_type) }}>
                            {getContentTypeLabel(draft.content_type)}
                          </span>
                        )}
                      </td>
                      <td className="cr-date-cell">{formatDate(draft.responded_at || draft.client_decision_at || draft.sent_to_client_at || draft.updated_at || draft.created_at)}</td>
                      <td className="cr-approve-cell">
                        {draft.status === 'approved' && (
                          <span className="history-badge approved">Approved</span>
                        )}
                        {draft.status === 'denied' && (
                          <span className="history-badge denied">Denied</span>
                        )}
                        {draft.status === 'revision_requested' && (
                          <span className="history-badge revision">Revision Requested</span>
                        )}
                      </td>
                      <td className="cr-comment-cell">
                        <button
                          className="cr-comment-icon-btn"
                          onClick={(e) => {
                            e.preventDefault();
                            e.stopPropagation();
                            setCommentModalState({
                              open: true,
                              draftId: draft._id,
                              comments: draft.comments || []
                            });
                          }}
                          title="View comments"
                          type="button"
                        >
                          <Icon name="comment-empty" size="1.5rem" />
                          {draft.comments && draft.comments.length > 0 && (
                            <span className="cr-comment-count-badge">{draft.comments.length}</span>
                          )}
                        </button>
                      </td>
                      <td className="cr-view-cell">
                        <button
                          className="cr-view-btn"
                          onClick={() => setViewingDraft(draft)}
                          title="View content preview"
                        >
                          <Icon name="go-to-link" size="1.75rem" />
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )
        ) : (
          /* Review Tab */
          contentLoading ? (
            <div className="client-review-loading">
              <div className="client-review-spinner"></div>
              <p>Loading content...</p>
            </div>
          ) : contentDrafts.length === 0 ? (
            <div className="client-review-empty">
              <div className="client-review-empty-icon"><Icon name="content" size="3rem" /></div>
              <h3>No Content Pending Review</h3>
              <p>New content will show up here as soon as it's ready for you to review.</p>
            </div>
          ) : (
          <div className="client-review-table-card">
            <table className="client-review-table">
              <thead>
                <tr>
                  <th>Brand</th>
                  <th>Content</th>
                  <th>Preview</th>
                  <th>Type</th>
                  <th>Received</th>
                  <th>Actions</th>
                  <th>Comments</th>
                  <th>View</th>
                </tr>
              </thead>
              <tbody>
                {contentDrafts.map((draft) => (
                  <tr key={draft._id}>
                    <td className="cr-brand-cell">
                      {draft.brand?.icon_url ? (
                        <BrandIcon src={draft.brand.icon_url} alt={draft.brand?.name} className="cr-brand-logo" />
                      ) : (
                        <div className="cr-brand-placeholder">
                          <span>{(draft.brand?.name || 'B').charAt(0).toUpperCase()}</span>
                        </div>
                      )}
                    </td>
                    <td className="cr-post-cell">
                      <div className="cr-post-content">
                        <strong>{draft.headline || draft.title || 'Untitled'}</strong>
                        <span className="cr-org-name">{draft.organization?.name || draft.brand?.name || ''}</span>
                      </div>
                    </td>
                    <td className="cr-synopsis-cell">
                      <span className="cr-synopsis-text">
                        {draft.body || 'No preview available'}
                      </span>
                    </td>
                    <td className="cr-type-cell">
                      {draft.content_type && (
                        <span className="cr-type-badge" style={{ backgroundColor: getContentTypeBadgeColor(draft.content_type) }}>
                          {getContentTypeLabel(draft.content_type)}
                        </span>
                      )}
                    </td>
                    <td className="cr-date-cell">{formatDate(draft.sent_to_client_at || draft.created_at)}</td>
                    <td className="cr-approve-cell">
                      <div className="cr-approve-buttons">
                        <button
                          className="cr-approve-btn approve"
                          onClick={() => handleContentDecision(draft._id, 'approved')}
                          title="Approve"
                        >
                          <Icon name="ok" size="1.25rem" />
                        </button>
                        <button
                          className="cr-approve-btn flag"
                          onClick={() => handleContentDecision(draft._id, 'revision_requested')}
                          title="Request revision"
                        >
                          <Icon name="flag" size="1.25rem" />
                        </button>
                        <button
                          className="cr-approve-btn deny"
                          onClick={() => handleContentDecision(draft._id, 'denied')}
                          title="Deny"
                        >
                          <Icon name="cancel" size="1.25rem" />
                        </button>
                      </div>
                    </td>
                    <td className="cr-comment-cell">
                      <button
                        className="cr-comment-icon-btn"
                        onClick={(e) => {
                          e.preventDefault();
                          e.stopPropagation();
                          setCommentModalState({
                            open: true,
                            draftId: draft._id,
                            comments: draft.comments || []
                          });
                        }}
                        title="Add comment"
                        type="button"
                      >
                        <Icon name="comment-empty" size="1.5rem" />
                        {draft.comments && draft.comments.length > 0 && (
                          <span className="cr-comment-count-badge">{draft.comments.length}</span>
                        )}
                      </button>
                    </td>
                    <td className="cr-view-cell">
                      <button
                        className="cr-view-btn"
                        onClick={() => setViewingDraft(draft)}
                        title="View content preview"
                      >
                        <Icon name="go-to-link" size="1.75rem" />
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          )
        )}
      </div>
    </>
  );

  const renderOverview = () => (
    <div className="content-section">
      <h2>Client Overview</h2>
      <div className="client-grid">
        <div className="client-card overview-card">
          <h3>Your Portfolio</h3>
          {dashboardData && (
            <div className="overview-stats">
              <div className="stat-item">
                <span className="stat-number">{dashboardData.data?.organizations || 0}</span>
                <span className="stat-label">Organizations</span>
              </div>
              <div className="stat-item">
                <span className="stat-number">{dashboardData.data?.total_brands || 0}</span>
                <span className="stat-label">Total Brands</span>
              </div>
            </div>
          )}
        </div>

        <div className="client-card">
          <h3>Quick Actions</h3>
          <div className="quick-actions">
            {!isProxyMode && (
              <button
                className="action-btn primary"
                onClick={() => setActiveSection('calendar')}
              >
                Calendar
              </button>
            )}
            <button
              className="action-btn secondary"
              onClick={() => setActiveSection('brands')}
            >
              View All Brands
            </button>
            <button
              className="action-btn secondary"
              onClick={() => setActiveSection('organizations')}
            >
              View Organizations
            </button>
          </div>
        </div>

        <div className="client-card">
          <h3>Account Information</h3>
          <div className="status-overview">
            <div className="status-item">
              <span className="status-label">Name:</span>
              <span className="status-value">{user.name}</span>
            </div>
            <div className="status-item">
              <span className="status-label">Email:</span>
              <span className="status-value">{user.email}</span>
            </div>
            <div className="status-item">
              <span className="status-label">Operator:</span>
              <span className="status-value">{dashboardData?.operator?.name || 'Unassigned'}</span>
            </div>
          </div>
        </div>
      </div>
    </div>
  );

  const renderOrganizations = () => (
    <div className="content-section">
      <h2>Your Organizations</h2>
      <div className="entity-grid">
        {dashboardData?.organizations && dashboardData.organizations.length > 0 ? (
          dashboardData.organizations.map(org => (
            <div key={org.id || org._id} className="entity-card">
              <h3>{org.name || org.organization_name}</h3>
              <div className="entity-details">
                <div className="detail-row">
                  <span className="detail-label">Brands:</span>
                  <span className="detail-value">{org.brands?.length || 0}</span>
                </div>
                <div className="detail-row">
                  <span className="detail-label">Created:</span>
                  <span className="detail-value">
                    {org.created_at ? new Date(org.created_at).toLocaleDateString() : 'N/A'}
                  </span>
                </div>
              </div>
            </div>
          ))
        ) : (
          <div className="no-data">
            <h3>No Organizations</h3>
            <p>You don't have any organizations yet. Contact your account manager to set up organizations.</p>
          </div>
        )}
      </div>
    </div>
  );

  const renderBrands = () => (
    <div className="content-section">
      <h2>All Your Brands</h2>
      <div className="entity-grid">
        {dashboardData?.brands && dashboardData.brands.length > 0 ? (
          dashboardData.brands.map(brand => (
            <div key={brand.id || brand._id} className="entity-card">
              <h3>{brand.name || brand.brand_name}</h3>
              <div className="entity-details">
                <div className="detail-row">
                  <span className="detail-label">Organization:</span>
                  <span className="detail-value">{brand.organization_name || 'N/A'}</span>
                </div>
                <div className="detail-row">
                  <span className="detail-label">Created:</span>
                  <span className="detail-value">
                    {brand.created_at ? new Date(brand.created_at).toLocaleDateString() : 'N/A'}
                  </span>
                </div>
              </div>
            </div>
          ))
        ) : (
          <div className="no-data">
            <h3>No Brands</h3>
            <p>You don't have any brands yet. Brands are created within your organizations.</p>
          </div>
        )}
      </div>
    </div>
  );

  const renderSettings = () => (
    <div className="content-section">
      <h2>Account Settings</h2>
      <div className="settings-grid">
        <div className="settings-card">
          <h3>Profile Information</h3>
          <div className="profile-details">
            <div className="detail-row">
              <span className="detail-label">Name:</span>
              <span className="detail-value">{user.name}</span>
            </div>
            <div className="detail-row">
              <span className="detail-label">Email:</span>
              <span className="detail-value">{user.email}</span>
            </div>
            <div className="detail-row">
              <span className="detail-label">Account Type:</span>
              <span className="detail-value">Client</span>
            </div>
            <div className="detail-row">
              <span className="detail-label">Member Since:</span>
              <span className="detail-value">
                {user.created_at ? new Date(user.created_at).toLocaleDateString() : 'N/A'}
              </span>
            </div>
          </div>
        </div>
      </div>
    </div>
  );

  if (loading) {
    return (
      <Layout user={user} activeSection={activeSection} onSectionChange={setActiveSection} onLogout={onLogout} onOpenWelcome={handleOpenWelcome} isProxyMode={isProxyMode} showOrgSelector={false}>
        <div className="loading-container">
          <div className="loading-spinner"></div>
          <p>Loading dashboard...</p>
        </div>
      </Layout>
    );
  }

  // Determine if org/brand context is needed for current section
  // Clients need context for more screens since they're restricted to their assigned org/brand
  const needsOrgBrandContext = () => {
    // In proxy mode, show selectors on the same sections as real clients
    // This ensures the proxy view accurately reflects what the client sees
    // For real clients (not proxy mode), only show on specific sections
    const sectionsNeedingContext = [
      'client-review-ideas',
      'review-content',
      'brand-builder'
    ];
    return sectionsNeedingContext.includes(activeSection);
  };

  const showContextSelectors = needsOrgBrandContext();

  return (
    <>
      <Layout
        user={user}
        activeSection={activeSection}
        onSectionChange={setActiveSection}
        contextMenuContent={showContextSelectors ? <h2>{getContextMenuTitle()}</h2> : null}
        onLogout={onLogout}
        onOpenWelcome={handleOpenWelcome}
        onOpenOnboarding={handleOpenOnboarding}
        isProxyMode={isProxyMode}
        showOrgSelector={showContextSelectors}
      >
      {warning && (
        <div className="warning-banner">
          <p>{warning}</p>
        </div>
      )}
      {renderContent()}

      {/* Revision Comment Modal */}
      {revisionModal.open && (
        <div className="revision-modal-overlay" onClick={() => setRevisionModal({ open: false, type: null, decision: null, itemId: null, docId: null, ideaId: null })}>
          <div className="revision-modal" onClick={(e) => e.stopPropagation()}>
            <div className="revision-modal-header">
              <h3>{revisionModal.decision === 'denied' ? 'Decline' : 'Request Revision'}</h3>
              <button
                className="modal-close-btn"
                onClick={() => setRevisionModal({ open: false, type: null, decision: null, itemId: null, docId: null, ideaId: null })}
                aria-label="Close"
              />
            </div>
            <div className="revision-modal-content">
              <p className="revision-modal-description">
                {revisionModal.decision === 'denied'
                  ? 'Please explain why you are declining this so the operator understands your decision.'
                  : 'Please explain what needs to be revised so the operator can make the necessary changes.'}
              </p>
              <div className="revision-comment-user-info">
                {user.picture ? (
                  <img
                    src={user.picture}
                    alt={user.name}
                    className="revision-profile-pic"
                    referrerPolicy="no-referrer"
                  />
                ) : (
                  <div className="revision-profile-placeholder">
                    <span>{user.name?.charAt(0).toUpperCase() || 'U'}</span>
                  </div>
                )}
                <span className="revision-user-name">{user.name || 'User'}</span>
              </div>
              <textarea
                value={revisionComment}
                onChange={(e) => setRevisionComment(e.target.value)}
                placeholder={revisionModal.decision === 'denied'
                  ? "Enter your reason for declining..."
                  : "Enter your feedback or what needs to be revised..."}
                rows={5}
                autoFocus
              />
              <div className="revision-modal-actions">
                <button
                  className="btn-cancel"
                  onClick={() => {
                    setRevisionModal({ open: false, type: null, decision: null, itemId: null, docId: null, ideaId: null });
                    setRevisionComment('');
                  }}
                >
                  Cancel
                </button>
                <button
                  className={`btn-submit ${!revisionComment.trim() ? 'disabled' : ''}`}
                  onClick={handleRevisionSubmit}
                  disabled={!revisionComment.trim()}
                >
                  {revisionModal.decision === 'denied' ? 'Submit Decline' : 'Send Revision Request'}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
      </Layout>

      {!isProxyMode && (
        <>
          <WelcomeModal
            isOpen={showWelcomeModal}
            onClose={() => setShowWelcomeModal(false)}
            onComplete={handleWelcomeComplete}
          />
          <OnboardingRoleSelector
            isOpen={showOnboardingRoleSelector}
            onClose={() => setShowOnboardingRoleSelector(false)}
            onSelectRole={handleRoleSelect}
          />
          <OnboardingWizard
            isOpen={showOnboardingWizard}
            onClose={() => {
              setShowOnboardingWizard(false);
              setSelectedOnboardingRole(null);
            }}
            onComplete={handleOnboardingComplete}
            onSkip={handleOnboardingSkip}
            user={user}
            overrideRole={selectedOnboardingRole}
            onSectionChange={setActiveSection}
            currentSection={activeSection}
          />
        </>
      )}

      {/* Comment Modal */}
      <CommentModal
        isOpen={commentModalState.open}
        onClose={() => setCommentModalState({ open: false, draftId: null, comments: [] })}
        itemId={commentModalState.draftId}
        itemType="content-draft"
        comments={commentModalState.comments}
        user={user}
        isProxyMode={isProxyMode}
        onCommentAdded={(newComment) => {
          setContentDrafts(prev => prev.map(draft =>
            draft._id === commentModalState.draftId
              ? { ...draft, comments: [...(draft.comments || []), newComment] }
              : draft
          ));
        }}
        onCommentDeleted={(commentId) => {
          setContentDrafts(prev => prev.map(draft =>
            draft._id === commentModalState.draftId
              ? { ...draft, comments: (draft.comments || []).filter(c => (c._id || c.id) !== commentId) }
              : draft
          ));
        }}
      />

      {/* Idea Comment Modal */}
      <CommentModal
        isOpen={ideaCommentModalState.open}
        onClose={() => setIdeaCommentModalState({ open: false, docId: null, ideaId: null, comments: [] })}
        itemId={ideaCommentModalState.ideaId}
        docId={ideaCommentModalState.docId}
        itemType="idea"
        comments={ideaCommentModalState.comments}
        user={user}
        isProxyMode={isProxyMode}
        onCommentAdded={(newComment) => {
          setIdeasItems(prev => prev.map(idea => {
            if (idea.docId === ideaCommentModalState.docId && idea.id === ideaCommentModalState.ideaId) {
              return { ...idea, comments: [...(idea.comments || []), newComment] };
            }
            return idea;
          }));
          setIdeasHistory(prev => prev.map(idea => {
            if (idea.docId === ideaCommentModalState.docId && idea.id === ideaCommentModalState.ideaId) {
              return { ...idea, comments: [...(idea.comments || []), newComment] };
            }
            return idea;
          }));
        }}
        onCommentDeleted={(commentId) => {
          setIdeasItems(prev => prev.map(idea => {
            if (idea.docId === ideaCommentModalState.docId && idea.id === ideaCommentModalState.ideaId) {
              return { ...idea, comments: (idea.comments || []).filter(c => (c._id || c.id) !== commentId) };
            }
            return idea;
          }));
          setIdeasHistory(prev => prev.map(idea => {
            if (idea.docId === ideaCommentModalState.docId && idea.id === ideaCommentModalState.ideaId) {
              return { ...idea, comments: (idea.comments || []).filter(c => (c._id || c.id) !== commentId) };
            }
            return idea;
          }));
        }}
      />
    </>
  );
};

export default ClientDashboard;